import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";

const CONFIG_LIST_URL = "mst-admin/config/list";
const CONFIG_SEED_URL = "mst-admin/config/seed";
const CONFIG_BULK_UPDATE_URL = "mst-admin/config/bulk-update";

const CATEGORY_LABELS = {
  pricing: "Pricing",
  scoring: "Scoring",
  features: "Feature Flags",
  notifications: "Notifications",
  feed: "Feed",
  banner: "Banner",
  mira_jyotishi: "Mira (Astrology Voice)",
  boost: "Boost",
  user_preferences: "User Preferences",
  general: "General",
};

const CATEGORY_ORDER = [
  "pricing", "scoring", "features", "feed", "notifications",
  "banner", "mira_jyotishi", "boost", "user_preferences", "general",
];

const AppConfigList = () => {
  const [configs, setConfigs] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [seeding, setSeeding] = useState(false);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    const res = await func.get(CONFIG_LIST_URL);
    if (res?.status === 200) {
      setConfigs(res.data?.configs || []);
      setEdits({});
    } else {
      toast.error("Failed to load config");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = configs.filter((c) => c.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  const handleEdit = (key, value) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
  };

  // Known structured-object shapes — render per-field inputs instead of a
  // raw JSON textarea. Anything not in this list falls back to JSON edit.
  // For Mira voice blocks the fields are dictated by jyotishiVoice.js.
  const _MIRA_FIELDS = ["title", "explain", "shubha", "madhyama", "ashubha", "tone"];
  const _isMiraVoice = (val) =>
    val && typeof val === "object" && !Array.isArray(val) &&
    ("title" in val || "explain" in val);

  // Stringify a config value for display in an input. Objects are rendered
  // as pretty JSON when they don't match a known shape.
  const _displayValue = (val) =>
    typeof val === "object" && val !== null
      ? JSON.stringify(val, null, 2)
      : String(val);

  // For Mira voice rows the edit state is the live object (not a string)
  // so per-field updates merge into a fresh object each keystroke.
  const _miraInit = (cfg) =>
    edits[cfg.key] !== undefined && typeof edits[cfg.key] === "object"
      ? edits[cfg.key]
      : { ...cfg.value };

  const _setMiraField = (key, field, fieldVal, baseObj) => {
    setEdits((prev) => ({
      ...prev,
      [key]: { ...baseObj, [field]: fieldVal },
    }));
  };

  // A row is dirty when the staged edit differs from the stored value.
  // For object edits we compare deep-equal via JSON.stringify (cheap + correct
  // for our config shapes).
  const _isDirty = (cfg) => {
    const e = edits[cfg.key];
    if (e === undefined) return false;
    if (typeof e === "object") {
      return JSON.stringify(e) !== JSON.stringify(cfg.value);
    }
    return e !== _displayValue(cfg.value);
  };

  const handleSaveCategory = async (cat) => {
    const items = grouped[cat] || [];
    const changed = items
      .filter(_isDirty)
      .map((c) => {
        let value = edits[c.key];
        // Per-field structured edits already arrive as an object.
        // Raw JSON-textarea edits (fallback for unknown shapes) arrive as
        // a string — parse back. Surface a toast on invalid JSON so the
        // admin doesn't silently overwrite a config with a broken string.
        if (typeof c.value === "object" && c.value !== null && typeof value === "string") {
          try {
            value = JSON.parse(value);
          } catch (e) {
            toast.error(`Invalid JSON for ${c.key}`);
            throw e;
          }
        }
        return { key: c.key, value, label: c.label };
      });

    if (!changed.length) {
      toast.info("No changes to save");
      return;
    }

    setSaving((prev) => ({ ...prev, [cat]: true }));
    const res = await func.post(CONFIG_BULK_UPDATE_URL, { updates: changed });
    if (res?.status === 200) {
      toast.success(`${CATEGORY_LABELS[cat] || cat} config saved`);
      fetchConfigs();
    } else {
      toast.error("Failed to save config");
    }
    setSaving((prev) => ({ ...prev, [cat]: false }));
  };

  const handleSeed = async () => {
    setSeeding(true);
    const res = await func.post(CONFIG_SEED_URL, {});
    if (res?.status === 200) {
      toast.success("Default config seeded");
      fetchConfigs();
    } else {
      toast.error("Seed failed");
    }
    setSeeding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
        <div className="max-w-4xl mx-auto space-y-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-32 mb-4" />
              {[...Array(4)].map((_, j) => (
                <div key={j} className="flex items-center gap-4 mb-3">
                  <div className="h-4 bg-gray-200 rounded w-40" />
                  <div className="h-8 bg-gray-200 rounded flex-1" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">App Config</h1>
            <p className="text-sm text-white/70 mt-0.5">Manage dynamic pricing, features, and app settings</p>
          </div>
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg transition disabled:opacity-50"
          >
            {seeding ? "Seeding..." : "Seed Defaults"}
          </button>
        </div>

        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="bg-white rounded-lg shadow-md mb-5">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800 capitalize">
                {CATEGORY_LABELS[cat] || cat}
              </h2>
              <button
                onClick={() => handleSaveCategory(cat)}
                disabled={saving[cat]}
                className="px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition disabled:opacity-50"
              >
                {saving[cat] ? "Saving..." : "Save Changes"}
              </button>
            </div>
            <div className="px-5 py-3 space-y-3">
              {items.map((cfg) => {
                const isObject = typeof cfg.value === "object" && cfg.value !== null;
                const isMira = _isMiraVoice(cfg.value);
                const isDirty = _isDirty(cfg);
                // Per-field Mira editor — title/explain/shubha/etc each on
                // their own row, only the present fields rendered.
                if (isMira) {
                  const obj = _miraInit(cfg);
                  // Stable field order: any KNOWN field first (in canonical
                  // order), then anything else the static voice happens to
                  // define (cancelled / parihara / future additions).
                  const fields = [
                    ..._MIRA_FIELDS.filter((f) => f in cfg.value),
                    ...Object.keys(cfg.value).filter((f) => !_MIRA_FIELDS.includes(f)),
                  ];
                  return (
                    <div key={cfg.key} className={`rounded-md border ${isDirty ? "border-amber-400 bg-amber-50/30" : "border-gray-200 bg-gray-50/50"} p-3`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-sm font-medium text-gray-700">{cfg.label || cfg.key}</div>
                          <div className="text-xs text-gray-400 font-mono break-all">{cfg.key}</div>
                        </div>
                        {isDirty && (
                          <span className="text-xs text-amber-600 font-medium whitespace-nowrap">unsaved</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {fields.map((field) => (
                          <div key={field}>
                            <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">{field}</label>
                            <textarea
                              value={obj[field] ?? ""}
                              onChange={(e) => _setMiraField(cfg.key, field, e.target.value, obj)}
                              rows={field === "title" ? 2 : 3}
                              spellCheck={true}
                              className="w-full text-sm px-3 py-2 border border-gray-300 bg-white rounded-md focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                // Scalar + unknown-object fallback (JSON textarea).
                const baseDisplay = _displayValue(cfg.value);
                const rawEdit = edits[cfg.key];
                const currentVal =
                  rawEdit === undefined
                    ? baseDisplay
                    : typeof rawEdit === "object"
                    ? JSON.stringify(rawEdit, null, 2)
                    : rawEdit;
                const isBoolean =
                  typeof cfg.value === "boolean" ||
                  currentVal === "true" ||
                  currentVal === "false";
                return (
                  <div key={cfg.key} className={`flex ${isObject ? "items-start" : "items-center"} gap-4`}>
                    <div className="w-56 flex-shrink-0">
                      <div className="text-sm font-medium text-gray-700">{cfg.label || cfg.key}</div>
                      <div className="text-xs text-gray-400 font-mono break-all">{cfg.key}</div>
                    </div>
                    <div className="flex-1">
                      {isObject ? (
                        <textarea
                          value={currentVal}
                          onChange={(e) => handleEdit(cfg.key, e.target.value)}
                          rows={Math.min(12, currentVal.split("\n").length + 1)}
                          spellCheck={false}
                          className={`w-full text-xs font-mono px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition ${
                            isDirty ? "border-amber-400 bg-amber-50" : "border-gray-300 bg-white"
                          }`}
                        />
                      ) : isBoolean ? (
                        <select
                          value={currentVal}
                          onChange={(e) => handleEdit(cfg.key, e.target.value)}
                          className={`w-full text-sm px-3 py-1.5 border rounded-md focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition ${
                            isDirty ? "border-amber-400 bg-amber-50" : "border-gray-300 bg-white"
                          }`}
                        >
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={currentVal}
                          onChange={(e) => handleEdit(cfg.key, e.target.value)}
                          className={`w-full text-sm px-3 py-1.5 border rounded-md focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition ${
                            isDirty ? "border-amber-400 bg-amber-50" : "border-gray-300 bg-white"
                          }`}
                        />
                      )}
                    </div>
                    {isDirty && (
                      <span className={`text-xs text-amber-600 font-medium whitespace-nowrap ${isObject ? "mt-2" : ""}`}>unsaved</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {!Object.keys(grouped).length && (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            <p className="mb-3">No config found.</p>
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              Seed Defaults
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppConfigList;
